import React, { useContext } from 'react';
import { css } from '@emotion/css';
import classNames from 'classnames';
import { Typography } from '@prezzee/components';

import { DrainContext } from 'contexts/DrainContext';
import { EMPTY_STRING } from 'common/utils/string';
import { customElementInteraction as Segment_customElementInteraction } from 'bindings/Segment.bs';
import { SearchInput } from 'components/library/SearchInput';
import { FilterTaxonomy } from 'api/queries/filter';

import FilterGroupMobile from './FilterGroupMobile';
import FilterGroupDesktop from './FilterGroupDesktop';

import { Color } from 'css/theme';

const classes = {
  root: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: ${Color.Lilac400};
    width: 100%;
    color: #1e1e1e;
    padding-bottom: 20px;
    height: 250px;
    align-items: center;
    @media (max-width: 1023px) {
      flex-direction: column;
      height: 300px;
    }
    @media (max-width: 592px) {
      height: 350px;
    },
    z-index: 999;
  `,
  title: css`
    font-size: 42px;
    line-height: 52px;
    font-weight: 700;
    text-align: center;
    padding: 20px;
    color: #1e1e1e;
    width: 100%;
    @media (min-width: 1023px) {
      padding-top: 20px;
    }
  `,
  search: {
    root: (count: number) => css`
      display: flex;
      flex-direction: row;
      flex-basis: ${{
        0: '100%',
        1: '80%',
        2: '60%',
        3: '40%',
        4: '49%',
      }[count]};
      width: 50%;
      margin: 0 12px 0 0;
      @media (max-width: 1287px) {
        flex-basis: 50%;
      }
      @media (max-width: 1023px) {
        margin: 0;
        width: 100%;
      }
    `,
    item: css`
      width: 100%;
    `,
  },
  filter: {
    root: (count: number) => css`
      display: flex;
      flex-direction: row;
      @media (max-width: 1023px) {
        flex-direction: column;
      }
    `,
    rootFilter: (count: number) => css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      flex-basis: ${{
        0: '0',
        1: '20%',
        2: '40%',
        3: '60%',
        4: '80%',
      }[count]};
      @media (max-width: 1287px) {
        flex-basis: 50%;
      }
      @media (max-width: 1023px) {
        flex-basis: 100%;
        width: 100%;
        margin: 20px 0 0;
      }
    `,
    desktop: css`
      display: flex;
      @media (max-width: 1287px) {
        display: none;
      }
    `,
    mobile: css`
      display: none;
      flex: 1;
      @media (max-width: 1287px) {
        display: flex;
      }
    `,
  },
  sort: {
    section: css`
      position: relative;
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
      padding-top: 140px;
      width: 200px;
      margin-left: auto;
      @media (max-width: 1287px) {
        display: none;
      }
    `,
    item: css``,
  },
};

type Props = {
  className: string;
  filterList?: FilterTaxonomy;
};

export const FilterSection: React.FC<Props> = ({ className, filterList }) => {
  const drain = useContext(DrainContext);

  return (
    <>
      <div className={classNames([classes.root, className])}>
        <div className={classNames(['container', 'px-4'])}>
          <div className={classes.title}>
            <Typography variant="h2" className={classes.title}>
              {filterList.root.title}
            </Typography>
          </div>
          <div
            className={classes.filter.root(
              Object.values(filterList.elements).filter(({ switched }) => switched).length
            )}
          >
            <div
              className={classes.search.root(
                Object.values(filterList.elements).filter(({ switched }) => switched).length
              )}
            >
              <div className={classes.search.item}>
                <SearchInput
                  className="flex-1 relative"
                  filterList={filterList}
                  setSearch={drain.setSearch}
                  defaultValue={drain.search || EMPTY_STRING}
                  trackSearchReset={() =>
                    Segment_customElementInteraction({
                      element: 'clear_search',
                      journey: 'classic_product_search',
                    })
                  }
                />
              </div>
            </div>
            <div
              className={classes.filter.rootFilter(
                Object.values(filterList.elements).filter(({ switched }) => switched).length
              )}
            >
              <FilterGroupMobile className={classes.filter.mobile} filterList={filterList} />
              <FilterGroupDesktop className={classes.filter.desktop} filterList={filterList} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
